/**
 * Fund Info Section Component
 *
 * Displays key fund metrics in a grid layout including:
 * - AUM (Assets Under Management)
 * - Expense Ratio
 * - Exit Load
 * - Category
 * - Plan Type
 * - Launch Date
 * - Benchmark
 * - Min SIP Amount
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatINRCompact, formatDate } from "@/lib/utils";
import {
  Building2,
  Percent,
  DoorOpen,
  TrendingUp,
  Target,
  Calendar,
  CreditCard,
  Tag,
} from "lucide-react";
import type { FundCategory, FundPlanType } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund info data for the section
 */
export interface FundInfoSectionData {
  /** Assets Under Management in crores */
  aum: number;
  /** Expense ratio as percentage */
  expenseRatio: number;
  /** Exit load description */
  exitLoad: string;
  /** Fund category */
  category: FundCategory;
  /** Plan type (direct/regular) */
  planType: FundPlanType;
  /** Fund launch date (ISO string) */
  launchDate: string;
  /** Benchmark index name */
  benchmarkIndex: string;
  /** Minimum SIP amount in INR */
  minSipAmount: number;
  /** Lock-in period in days */
  lockInPeriod?: number;
}

/**
 * Props for the FundInfoSection component
 */
export interface FundInfoSectionProps {
  /** Fund info data */
  data: FundInfoSectionData;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Category labels for display
 */
const CATEGORY_LABELS: Record<FundCategory, string> = {
  "equity-large-cap": "Large Cap",
  "equity-mid-cap": "Mid Cap",
  "equity-small-cap": "Small Cap",
  "equity-flexi-cap": "Flexi Cap",
  "equity-multi-cap": "Multi Cap",
  "equity-elss": "ELSS (Tax Saver)",
  "equity-sectoral": "Sectoral/Thematic",
  "equity-global": "Global/International",
  "debt-liquid": "Liquid",
  "debt-short-term": "Short Duration",
  "debt-medium-term": "Medium Duration",
  "debt-long-term": "Long Duration",
  "debt-gilt": "Gilt",
  "debt-corporate": "Corporate Bond",
  "hybrid-aggressive": "Aggressive Hybrid",
  "hybrid-balanced": "Balanced Hybrid",
  "hybrid-conservative": "Conservative Hybrid",
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format AUM for display
 */
function formatAUM(aumInCrores: number): string {
  if (aumInCrores >= 1000) {
    return `${(aumInCrores / 1000).toFixed(2)}K Cr`;
  }
  return `${aumInCrores.toLocaleString("en-IN")} Cr`;
}

/**
 * Get expense ratio color class
 */
function getExpenseRatioColor(ratio: number): string {
  if (ratio <= 0.5) return "text-green-600 dark:text-green-400";
  if (ratio <= 1.0) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

// ============================================================================
// Subcomponents
// ============================================================================

interface InfoCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
  valueClassName?: string;
}

/**
 * Individual info card in the grid
 */
function InfoCard({ icon, label, value, subValue, valueClassName }: InfoCardProps) {
  return (
    <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
      <div className="flex-shrink-0 p-2 bg-background rounded-md">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className={cn("text-sm font-semibold mt-0.5 truncate", valueClassName)}>
          {value}
        </p>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Fund Info Section
 *
 * Displays key fund metrics in a responsive grid layout.
 *
 * @example
 * ```tsx
 * <FundInfoSection
 *   data={{
 *     aum: 42567,
 *     expenseRatio: 0.45,
 *     exitLoad: "1% if redeemed within 1 year",
 *     category: "equity-flexi-cap",
 *     planType: "direct",
 *     launchDate: "2013-05-24",
 *     benchmarkIndex: "NIFTY 500 TRI",
 *     minSipAmount: 1000,
 *   }}
 * />
 * ```
 */
export function FundInfoSection({ data, className }: FundInfoSectionProps) {
  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Fund Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {/* AUM */}
          <InfoCard
            icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
            label="AUM"
            value={`Rs. ${formatAUM(data.aum)}`}
          />

          {/* Expense Ratio */}
          <InfoCard
            icon={<Percent className="h-4 w-4 text-muted-foreground" />}
            label="Expense Ratio"
            value={`${data.expenseRatio.toFixed(2)}%`}
            valueClassName={getExpenseRatioColor(data.expenseRatio)}
          />

          {/* Exit Load */}
          <InfoCard
            icon={<DoorOpen className="h-4 w-4 text-muted-foreground" />}
            label="Exit Load"
            value={data.exitLoad || "Nil"}
          />

          {/* Category */}
          <InfoCard
            icon={<Tag className="h-4 w-4 text-muted-foreground" />}
            label="Category"
            value={CATEGORY_LABELS[data.category]}
          />

          {/* Plan Type */}
          <InfoCard
            icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
            label="Plan Type"
            value={data.planType === "direct" ? "Direct Plan" : "Regular Plan"}
          />

          {/* Launch Date */}
          <InfoCard
            icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
            label="Launch Date"
            value={formatDate(data.launchDate)}
          />

          {/* Benchmark */}
          <InfoCard
            icon={<Target className="h-4 w-4 text-muted-foreground" />}
            label="Benchmark"
            value={data.benchmarkIndex}
          />

          {/* Min SIP Amount */}
          <InfoCard
            icon={<CreditCard className="h-4 w-4 text-muted-foreground" />}
            label="Min. SIP"
            value={formatINRCompact(data.minSipAmount)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for FundInfoSection
 */
export function FundInfoSectionSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn("w-full", className)}>
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-32" />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg"
            >
              <Skeleton className="h-8 w-8 rounded-md flex-shrink-0" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default FundInfoSection;
